// Mixin that resolves the active theme (light / dark / system) and applies
// a data-theme attribute on <html> so CSS can scope dark-mode overrides.
//
// Persists to localStorage['eden-theme'] — the SAME key the Vue 3 useTheme
// composable uses — so the theme choice carries across the Vue 2 / Vue 3
// module boundary (each family applies its own attribute on load).

const LS_KEY = 'eden-theme'

export default {
  computed: {
    themeMode: {
      get() {
        return this.$store.getters.uiThemeMode || 'light'
      },
      set(value) {
        this.$store.commit('uiThemeMode', value)
        try { localStorage.setItem(LS_KEY, value) } catch (e) { /* ignore */ }
      },
    },

    themeModeOptions() {
      return [
        { value: 'light', text: 'Light' },
        { value: 'dark', text: 'Dark' },
        { value: 'system', text: 'System (auto)' },
      ]
    },

    resolvedTheme() {
      if (this.themeMode === 'system') {
        return this.systemPrefersDark ? 'dark' : 'light'
      }
      return this.themeMode
    },

    isDark() {
      return this.resolvedTheme === 'dark'
    },
  },

  data() {
    return {
      systemPrefersDark: window.matchMedia('(prefers-color-scheme: dark)').matches,
    }
  },

  watch: {
    resolvedTheme: {
      immediate: true,
      handler(theme) {
        document.documentElement.setAttribute('data-theme', theme)
        document.documentElement.classList.toggle('eden-dark', theme === 'dark')
      },
    },
  },

  created() {
    // Sync from the shared key (may have been set by a Vue 3 module or a prior
    // Vue 2 session) so the theme choice persists across the family boundary.
    try {
      const saved = localStorage.getItem(LS_KEY)
      if (saved && ['light', 'dark', 'system'].includes(saved) && saved !== this.$store.getters.uiThemeMode) {
        this.$store.commit('uiThemeMode', saved)
      }
    } catch (e) { /* ignore */ }

    // Instance-scoped listener — this mixin is applied to multiple components
    // that mount concurrently (TheContainer + the nested header dropdown), so a
    // module-level listener would be overwritten and leaked.
    this.$_themeMq = window.matchMedia('(prefers-color-scheme: dark)')
    this.$_themeMqListener = (e) => { this.systemPrefersDark = e.matches }
    this.$_themeMq.addEventListener('change', this.$_themeMqListener)
  },

  beforeDestroy() {
    if (this.$_themeMq && this.$_themeMqListener) {
      this.$_themeMq.removeEventListener('change', this.$_themeMqListener)
      this.$_themeMq = null
      this.$_themeMqListener = null
    }
  },
}
